// Laser-cut design picker — preset library + custom PNG/SVG upload.
import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Upload, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { LASER_CUT_PRESETS, LASER_CUT_CATEGORIES, getLaserPreset } from "@/lib/laserCutPresets";

const MAX_UPLOAD_BYTES = 2 * 1024 * 1024; // 2 MB — plenty for SVG/PNG artwork

export const LaserCutPresetPicker = ({
  preset, designName, uploadUrl, sheetThicknessMm, colourHex = "#334155",
  onChange, // ({preset, designName, uploadUrl, sheetThicknessMm})
}) => {
  const fileRef = useRef(null);
  const [selectedCat, setSelectedCat] = useState("Nature");
  const [uploading, setUploading] = useState(false);
  const current = getLaserPreset(preset);
  const filtered = LASER_CUT_PRESETS.filter((p) => p.category === selectedCat);

  const setField = (patch) => onChange({ preset, designName, uploadUrl, sheetThicknessMm, ...patch });

  const onPickPreset = (value) => {
    const p = getLaserPreset(value);
    setField({ preset: value, designName: p.value === "custom" ? designName : p.label });
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_UPLOAD_BYTES) { toast.error(`File too large — keep under 2 MB (yours is ${(file.size / 1024 / 1024).toFixed(1)} MB)`); return; }
    if (!["image/png", "image/svg+xml", "image/jpeg", "image/webp"].includes(file.type)) {
      toast.error("Please upload a PNG, SVG, JPG or WebP file"); return;
    }
    setUploading(true);
    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const fr = new FileReader();
        fr.onload = () => resolve(fr.result);
        fr.onerror = reject;
        fr.readAsDataURL(file);
      });
      setField({ preset: "custom", uploadUrl: dataUrl, designName: designName || file.name.replace(/\.[^.]+$/, "") });
      toast.success(`Uploaded — ${file.name}`);
    } catch {
      toast.error("Could not read the file");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="space-y-3" data-testid="laser-cut-picker">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2.5">
        <div>
          <Label className="stamp-label text-slate-600">Design category</Label>
          <Select value={selectedCat} onValueChange={setSelectedCat}>
            <SelectTrigger className="rounded-sm h-9 mt-1" data-testid="laser-cut-category"><SelectValue /></SelectTrigger>
            <SelectContent>
              {LASER_CUT_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="stamp-label text-slate-600">Design name (on quote)</Label>
          <Input value={designName || ""} onChange={(e) => setField({ designName: e.target.value })}
            placeholder="e.g. Smith family logo" className="rounded-sm h-9 mt-1" data-testid="laser-cut-design-name" />
        </div>
        <div>
          <Label className="stamp-label text-slate-600">Sheet thickness (mm)</Label>
          <Input type="number" min="1" step="0.5" value={sheetThicknessMm || 3}
            onChange={(e) => setField({ sheetThicknessMm: Number(e.target.value) || 3 })}
            className="rounded-sm h-9 mt-1 font-mono" data-testid="laser-cut-thickness" />
        </div>
      </div>

      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2" data-testid="laser-cut-preset-grid">
        {filtered.map((p) => {
          const active = p.value === preset;
          return (
            <button key={p.value} type="button" onClick={() => onPickPreset(p.value)}
              className={`aspect-square rounded-sm border-2 p-1 bg-white hover:border-cyan-400 transition-colors ${active ? "border-cyan-500 ring-2 ring-cyan-200" : "border-slate-200"}`}
              title={p.label} data-testid={`laser-cut-preset-${p.value}`}>
              {p.value === "custom" ? (
                <div className="w-full h-full flex flex-col items-center justify-center text-slate-400">
                  <Upload size={20} />
                  <span className="text-[9px] mt-1 uppercase tracking-wider">Upload</span>
                </div>
              ) : (
                <svg viewBox="0 0 100 100" className="w-full h-full">
                  <g dangerouslySetInnerHTML={{ __html: p.render(colourHex) }} />
                </svg>
              )}
              <div className="text-[9px] text-slate-500 text-center truncate mt-0.5 leading-tight">{p.label.split(" — ")[1] || p.label}</div>
            </button>
          );
        })}
      </div>

      {preset === "custom" && (
        <div className="border border-dashed border-cyan-300 rounded-sm p-3 bg-cyan-50/40" data-testid="laser-cut-upload-area">
          {uploadUrl ? (
            <div className="flex items-center gap-3">
              <div className="w-16 h-16 rounded-sm border border-slate-200 bg-white overflow-hidden shrink-0">
                <img src={uploadUrl} alt="Uploaded laser-cut pattern" className="w-full h-full object-cover" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-medium text-slate-800 truncate">{designName || "Uploaded design"}</div>
                <div className="text-[10px] text-slate-500">Custom pattern loaded. Client will see this pattern rendered inside their fence panel.</div>
              </div>
              <Button type="button" size="sm" variant="ghost" onClick={() => setField({ uploadUrl: "" })}
                className="rounded-sm h-8 w-8 p-0 text-slate-400 hover:text-rose-600" data-testid="laser-cut-remove-upload">
                <Trash2 size={14} />
              </Button>
            </div>
          ) : (
            <div className="text-center py-3">
              <Upload size={20} className="mx-auto text-slate-400 mb-1.5" />
              <p className="text-xs text-slate-600 mb-2">Upload a PNG or SVG of the client's pattern</p>
              <input ref={fileRef} type="file" accept="image/png,image/svg+xml,image/jpeg,image/webp" onChange={onUpload}
                className="hidden" data-testid="laser-cut-file-input" />
              <Button type="button" size="sm" onClick={() => fileRef.current?.click()} disabled={uploading}
                className="rounded-sm bg-cyan-600 hover:bg-cyan-700 text-white h-8" data-testid="laser-cut-upload-btn">
                {uploading ? "Reading…" : "Choose file"}
              </Button>
              <p className="text-[10px] text-slate-400 mt-1.5">Max 2 MB — PNG, SVG, JPG or WebP</p>
            </div>
          )}
        </div>
      )}

      <div className="text-[11px] text-slate-500 bg-slate-50 rounded-sm p-2.5" data-testid="laser-cut-summary">
        <span className="font-semibold text-slate-700">{current?.label || "-"}</span>
        {" · "}<span className="text-cyan-700 font-medium">{sheetThicknessMm || 3} mm sheet</span>
        {" · "}Client sees this exact pattern on their design preview and PDF; workshop receives a DXF-ready spec in the cut list.
      </div>
    </div>
  );
};

export default LaserCutPresetPicker;
